import { io, Socket } from 'socket.io-client';
import { authEvents, getLastActivityAt, getValidAccessToken } from 'services/auth';
import { ACTIVITY_WINDOW_MS } from 'services/auth/constants';

const socketURL = import.meta.env.VITE_API_BASE_URL || '';

// socket.io invokes `auth` on every (re)connect handshake, so the token is always read live.
export const initializeSocketConnection = () => io(`${socketURL}`, {
  auth: (cb) => {
    getValidAccessToken()
      .then((token) => cb({ token }))
      .catch(() => cb({}));
  },
  transports: ['websocket'],
  path: '/clients',
});

const IDLE_EVENTS = ['pointerdown', 'keydown', 'visibilitychange'] as const;

// Reconnects on rotation and on server-side expiry disconnects (only while the user is active).
// Returns the teardown that removes every listener and subscription.
export const attachSocketAuthLifecycle = (socket: Socket): (() => void) => {
  let removeIdleListeners: (() => void) | null = null;

  const clearIdleListeners = () => {
    if (removeIdleListeners) removeIdleListeners();
    removeIdleListeners = null;
  };

  const reconnectOnNextActivity = () => {
    if (removeIdleListeners) return;
    const onActivity = (event: Event) => {
      if (event.type === 'visibilitychange' && document.visibilityState !== 'visible') return;
      clearIdleListeners();
      socket.connect();
    };
    IDLE_EVENTS.forEach((name) => document.addEventListener(name, onActivity, { capture: true }));
    removeIdleListeners = () => IDLE_EVENTS.forEach((name) => document.removeEventListener(name, onActivity, { capture: true }));
  };

  const onDisconnect = (reason: string) => {
    if (reason !== 'io server disconnect') return;
    if (Date.now() - getLastActivityAt() <= ACTIVITY_WINDOW_MS) {
      socket.connect();
    } else {
      reconnectOnNextActivity();
    }
  };

  const unsubscribeToken = authEvents.on('token-changed', () => {
    if (!socket.connected) return;
    socket.disconnect();
    socket.connect();
  });

  socket.on('disconnect', onDisconnect);

  return () => {
    unsubscribeToken();
    socket.off('disconnect', onDisconnect);
    clearIdleListeners();
  };
};
